/**
 * Impact advanced-filter document — a versioned value the web filter panel
 * edits and evaluates CLIENT-SIDE over the complete Impact index (the
 * places-grid modal-filters design; template: placesFilters.ts). One schema
 * + ONE evaluator, both in @releve/shared:
 *  - the web filter panel (live evaluation as controls change),
 *  - any future server consumer (reuse `applyImpactFilterDocument` — never
 *    build SQL WHERE clauses from the document).
 *
 * The document is AD HOC — it lives in component state for the session
 * (saved/shared presets were deliberately removed from the places build;
 * same posture here).
 *
 * v1 fields are `activity.<field>` over the MAPPED Impact grid rows, not
 * the raw view row: `effective_date` is mapper-computed (local calendar-day
 * coalesce of a DATE and two timestamptz inputs — see
 * sustainability_activity_index), so evaluation runs after the mapper,
 * before the grid shell.
 *
 * Extends the places condition vocabulary with a DATE-RANGE condition
 * (`yyyy-MM-dd` bounds, lexicographic compare — safe for ISO calendar
 * dates), which the places document didn't need.
 */
import { z } from "zod";
// ---------------------------------------------------------------------------
// Field vocabulary (v1)
// ---------------------------------------------------------------------------
export const IMPACT_FILTER_STRING_FIELDS = [
    "activity.row_kind",
    "activity.emission_scope_key",
    "activity.emission_table_id",
    "activity.effective_project_id",
];
export const IMPACT_FILTER_NUMERIC_FIELDS = [
    "activity.calculated_emissions",
];
export const IMPACT_FILTER_DATE_FIELDS = ["activity.effective_date"];
// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------
/** `in` = value is one of `values` (exact match); `is_empty` = value unset. */
const stringConditionSchema = z
    .object({
    field: z.enum(IMPACT_FILTER_STRING_FIELDS),
    op: z.enum(["in", "is_empty"]),
    values: z.array(z.string().min(1).max(200)).min(1).max(50).optional(),
})
    .refine((c) => c.op !== "in" || (c.values?.length ?? 0) > 0, {
    message: "op 'in' requires a non-empty values array",
});
/** Numeric range: `between` uses both bounds, `gte`/`lte` one. NULL never matches. */
const numericConditionSchema = z
    .object({
    field: z.enum(IMPACT_FILTER_NUMERIC_FIELDS),
    op: z.enum(["gte", "lte", "between"]),
    min: z.number().finite().optional(),
    max: z.number().finite().optional(),
})
    .refine((c) => (c.op === "gte" && c.min != null) ||
    (c.op === "lte" && c.max != null) ||
    (c.op === "between" && c.min != null && c.max != null), { message: "numeric condition is missing its bound(s)" });
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected yyyy-MM-dd");
/**
 * Calendar-date range over `yyyy-MM-dd` strings (lexicographic compare is
 * date order for this format). Distinct ops from the numeric family so the
 * evaluator can narrow on op alone.
 */
const dateConditionSchema = z
    .object({
    field: z.enum(IMPACT_FILTER_DATE_FIELDS),
    op: z.enum(["date_gte", "date_lte", "date_between"]),
    min: isoDate.optional(),
    max: isoDate.optional(),
})
    .refine((c) => (c.op === "date_gte" && c.min != null) ||
    (c.op === "date_lte" && c.max != null) ||
    (c.op === "date_between" && c.min != null && c.max != null), { message: "date condition is missing its bound(s)" });
export const impactFilterConditionSchema = z.union([
    stringConditionSchema,
    numericConditionSchema,
    dateConditionSchema,
]);
// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------
export const impactFilterDocumentSchema = z.object({
    version: z.literal(1),
    /** v1 is AND-only; `match` exists so v2 can add 'any' without re-shaping. */
    match: z.literal("all"),
    conditions: z.array(impactFilterConditionSchema).max(20),
});
/** The empty document — "no filters active". */
export const EMPTY_IMPACT_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [],
};
/*
 * One accessor per field FAMILY, each an exhaustive switch over its
 * vocabulary. The numeric and date families have a single member today, so
 * reading `row.calculated_emissions` / `row.effective_date` inline would
 * behave identically — but then adding a second field would compile clean
 * and silently filter on the wrong column. With these switches, a new
 * vocabulary entry is a `TS2366: Function lacks ending return statement`
 * until it is handled here. That guarantee is the reason these exist; do not
 * collapse them back inline.
 */
function stringValue(row, field) {
    switch (field) {
        case "activity.row_kind":
            return row.row_kind;
        case "activity.emission_scope_key":
            return row.emission_scope_key ?? "";
        case "activity.emission_table_id":
            return row.emission_table_id ?? "";
        case "activity.effective_project_id":
            return row.effective_project_id ?? "";
    }
}
function numericValue(row, field) {
    switch (field) {
        case "activity.calculated_emissions":
            return row.calculated_emissions;
    }
}
/** `yyyy-MM-dd` (mapper-computed local day) — lexicographic compare is date order. */
function dateValue(row, field) {
    switch (field) {
        case "activity.effective_date":
            return row.effective_date;
    }
}
// The three condition families have disjoint op sets, so op alone narrows.
function matchesCondition(row, c) {
    switch (c.op) {
        case "in":
            return (c.values ?? []).includes(stringValue(row, c.field));
        case "is_empty":
            return stringValue(row, c.field) === "";
        case "gte":
        case "lte":
        case "between": {
            const v = numericValue(row, c.field);
            if (v == null)
                return false; // an unset number matches no range
            if (c.op === "gte")
                return v >= (c.min ?? Number.NEGATIVE_INFINITY);
            if (c.op === "lte")
                return v <= (c.max ?? Number.POSITIVE_INFINITY);
            return (v >= (c.min ?? Number.NEGATIVE_INFINITY) &&
                v <= (c.max ?? Number.POSITIVE_INFINITY));
        }
        case "date_gte":
        case "date_lte":
        case "date_between": {
            const d = dateValue(row, c.field);
            if (!d)
                return false; // an unset date matches no range
            if (c.op === "date_gte")
                return c.min == null || d >= c.min;
            if (c.op === "date_lte")
                return c.max == null || d <= c.max;
            return (c.min == null || d >= c.min) && (c.max == null || d <= c.max);
        }
    }
}
/**
 * Apply a filter document to mapped Impact rows (AND of all conditions —
 * v1 `match` is 'all' only). An empty document returns the input array
 * unchanged (identity — callers can rely on reference equality to skip
 * re-renders).
 */
export function applyImpactFilterDocument(rows, doc) {
    if (doc.conditions.length === 0)
        return rows;
    return rows.filter((row) => doc.conditions.every((c) => matchesCondition(row, c)));
}
/** Count of active conditions — the filter button's badge. */
export function countActiveImpactConditions(doc) {
    return doc.conditions.length;
}
//# sourceMappingURL=sustainabilityFilters.js.map